import { HomeGuiComponentsApiModel } from '@api/generated/models';
import { LicenceHolderApiService } from '@api/licence_holder_api.service';
import { AnalyticService } from '@application/analytic/analytic.service';
import { Box, BreakPoints, Button, Flex, Icon, SafeArea, Spacer, Spinner, Tooltip, useBreakpoint } from '@cellula/react';
import { primitiveColorTokens, tokens } from '@cellula/react-theme-patient';
import { Avatar } from '@components/aavatar.component';
import { AttentionCard } from '@components/attention_card/attention_card.component';
import { FlexibleIdentifier } from '@components/flexible_identifier/flexible_identifier.component';
import { SuspendedPresentation } from '@components/suspended_presentation.component';
import { withTranslation } from '@modules/i18n_and_l10n/with_translation';
import { useUser } from '@services/auth/mod';
import { useFeatureFlag } from '@services/feature_flag/use_feature_flag';
import { ComponentUnion, FlexibleComponentService } from '@services/flexible_component/flexible_component.service';
import { VisibaEnvelope } from '@services/http.service';
import { LicenceHolderService } from '@services/licence_holder.service';
import { Scoped, useResolve } from '@visiba/instantiation';
import { Presentation } from '@visiba/presentation';
import { clamp, rem } from '@visiba/std';
import { AnimatePresence, motion, useScroll } from 'framer-motion';
import { createContext, Fragment, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Balancer from 'react-wrap-balancer';
import styled from 'styled-components';
import { CareCompanionLaunch } from '../../../../patient/src/features/care_companion';
import { ViewFindReception } from '../../../../patient/src/views/view_find_reception/index';
import { Icon as IIcon, IconType as IIconType } from '../../../src/components/icon.component';

interface Props {
	__TEMP__onSignIn?: () => void;
	__TEMP__onNavigate?: (route: string) => void;
}

const StyledView = styled.div`
	position: relative;
	min-height: calc(100vh - var(--header-height) - 50px);
	width: 100%;
	background-color: #fff;
`;

const StyledUniqueButtons = styled.div`
	width: 100%;
	margin: 0 auto;
	max-width: var(--visiba-inner-width-medium);
	padding: ${rem(64)} var(--gap) ${rem(64)};
	min-height: 600px;
	overflow: hidden;

	${tokens.breakpointQuery.medium} {
		display: grid;
		grid-template-columns: repeat(1, 1fr);
		grid-column-gap: 10px;
		grid-row-gap: 48px;
	}
`;

export function Home({ __TEMP__onSignIn, __TEMP__onNavigate }: Props): JSX.Element {
	const user = useUser();
	const controller = useResolve(HomeController);
	const analyticService = useResolve(AnalyticService);
	const licenceHolderService = useResolve(LicenceHolderService);
	const shouldShowAttentionCard = useBreakpoint(BreakPoints.Medium | BreakPoints.Large);
	const useNewHome = useFeatureFlag('PatientWeb.UseNewHome');
	const featureCareCompanion = useFeatureFlag('Patient.CareCompanion.HomeChatLauncher');

	useEffect(
		function analyticQuirk_whenComponentMounts() {
			analyticService.trackPageView({ customTitle: `Landing Page` });
		},
		[analyticService],
	);

	return (
		<Fragment>
			{/* todo: This should be a provider, and not rendered like this... */}
			{/* todo: This should always be rendered but we'll constrain this to desktop... */}
			{shouldShowAttentionCard ? <AttentionCard /> : null}

			{useNewHome ? (
				<ViewFindReception __TEMP__onNavigate={__TEMP__onNavigate} />
			) : (
				<HomeContextForLegacyRouting.Provider value={{ legacySignInRouting: __TEMP__onSignIn }}>
					{user == null ? <Hero /> : null}

					<StyledView>
						<SuspendedPresentation
							controller={controller}
							fallback={
								<StyledUniqueButtons>
									<Spinner size='large' />
								</StyledUniqueButtons>
							}
						>
							{(presentation) => (
								<DataContainer
									presentation={presentation}
									licenceHolderId={licenceHolderService.id}
									onNavigate={__TEMP__onNavigate}
									onSignIn={__TEMP__onSignIn}
									showCareCompanion={featureCareCompanion}
									isAuthenticated={user != null}
								/>
							)}
						</SuspendedPresentation>
					</StyledView>
				</HomeContextForLegacyRouting.Provider>
			)}
		</Fragment>
	);
}

const HomeContextForLegacyRouting = createContext<{ legacySignInRouting?: () => void }>({ legacySignInRouting: undefined });

interface DataContainerProps {
	presentation: Presentation;
	licenceHolderId: number;
	onNavigate?: (route: string) => void;
	onSignIn?: () => void;
	showCareCompanion: boolean;
	isAuthenticated: boolean;
}

function DataContainer({
	presentation,
	licenceHolderId,
	onNavigate,
	onSignIn,
	showCareCompanion,
	isAuthenticated,
}: DataContainerProps): JSX.Element {
	const breakpoint = useBreakpoint();

	const buttonIdentifierList = useMemo(() => {
		let dataSet = presentation.componentsLarge;
		if (breakpoint === BreakPoints.Small) {
			dataSet = presentation.componentsSmall;
		}

		return dataSet.map((component) => <FlexibleIdentifier key={component.id} component={component} />);
	}, [breakpoint, presentation]);

	return (
		<StyledUniqueButtons data-testid='Home'>
			{showCareCompanion && onNavigate != null ? (
				<CareCompanionLaunch
					licenceHolderId={licenceHolderId}
					isAuthenticated={isAuthenticated}
					onNavigate={onNavigate}
					onSignIn={onSignIn}
				/>
			) : null}
			{buttonIdentifierList}
		</StyledUniqueButtons>
	);
}

// biome-ignore lint/suspicious/noRedeclare: suppressed due to migration - should be fixed
interface Presentation {
	componentsLarge: ComponentUnion[];
	componentsSmall: ComponentUnion[];
}

@Scoped()
class HomeController {
	public readonly presentation = Presentation.createConcurrent<Presentation>();

	constructor(
		private readonly flexibleComponentService: FlexibleComponentService,
		private readonly licenceHolderService: LicenceHolderService,
		private readonly licenceHolderApiService: LicenceHolderApiService,
	) {
		this.presentation.suspend(
			this.licenceHolderApiService.getHomeGuiComponents(this.licenceHolderService.id),
			this.parseBackendData.bind(this),
		);
	}

	private parseBackendData({ data }: VisibaEnvelope<HomeGuiComponentsApiModel>): Presentation {
		return {
			componentsLarge: data.LayoutLarge.map((guiComponent) => this.flexibleComponentService.produce(guiComponent.Type, guiComponent)),
			componentsSmall: data.LayoutSmall.map((guiComponent) => this.flexibleComponentService.produce(guiComponent.Type, guiComponent)),
		};
	}
}

const StyledHero = styled.div`
	display: flex;
	position: relative;
	height: calc(85vh - var(--header-height));
	width: 100%;
	min-height: 400px;
`;

const StyledBackground = styled.div`
	position: absolute;
	inset: 0;
	background: linear-gradient(252.15deg, ${primitiveColorTokens.primary['300']} 21.57%, ${primitiveColorTokens.primary['200']}) 88.57%
		center;
	z-index: -1;

	&:after {
		content: '';
		bottom: 0;
		left: 0;
		right: 0;
		position: absolute;
		height: 200px;
		background: linear-gradient(0, #fff 0%, transparent 100%);
		pointer-events: none;
		z-index: 0;

		${tokens.breakpointQuery.medium} {
			height: 400px;
		}
	}
`;

const StyledTitle = styled.h1`
	z-index: 1;

	font: ${tokens.text.heading.xLarge};
	color: ${tokens.color.content.default};

	font-weight: 700;
	letter-spacing: 0px;
	font-size: ${rem(35)};
	line-height: ${rem(35)};
	white-space: normal;

	${tokens.breakpointQuery.medium} {
		font-size: ${rem(55)};
		line-height: ${rem(66)};
	}
`;

const StyledDescription = styled.p`
	z-index: 1;
	max-width: 600px;

	font: ${tokens.text.heading.xSmall};
	color: ${tokens.color.content.muted};

	letter-spacing: 0px;
	white-space: normal;

	${tokens.breakpointQuery.medium} {
		font: ${tokens.text.heading.xSmall};
	}
`;

const InnerContainer = styled(motion.div)`
	max-width: var(--visiba-inner-width-medium);
	padding: 4rem var(--gap) 4rem;
	z-index: 1;
`;

function Hero(): JSX.Element {
	const breakpoint = useBreakpoint();
	const { legacySignInRouting } = useContext(HomeContextForLegacyRouting);

	return (
		<StyledHero>
			<SafeArea maxWidth='1320px'>
				<Box as='article' align='center' justify='center'>
					<InnerContainer
						initial={{
							opacity: 0,
						}}
						animate={{
							opacity: 1,
						}}
					>
						<StyledTitle>
							<Balancer>{withTranslation('PW.Component.Hero.Title')}</Balancer>
						</StyledTitle>

						<Spacer vertical={breakpoint !== BreakPoints.Large ? tokens.spacing.x2 : tokens.spacing.x3} />

						<StyledDescription>{withTranslation('PW.Component.Hero.Description')}</StyledDescription>

						<Spacer vertical={breakpoint !== BreakPoints.Large ? tokens.spacing.x4 : tokens.spacing.x5} />

						<Flex
							breakpointMediumAndUp={{
								align: 'flex-start',
							}}
						>
							<Button size='xLarge' variant='primary' onClick={legacySignInRouting}>
								{withTranslation('Common.Button.SignIn')}
							</Button>
						</Flex>
					</InnerContainer>
				</Box>
			</SafeArea>
			<ArrowDown />
			<StyledBackground />
			<Card icon='RR_man' delay={0.6} right={5} bottom={60} scale={0.75} bounciness={4} patter='reverse' />
			<Card icon='RR_myself' delay={0.9} right={12} bottom={30} scale={1.3} />
		</StyledHero>
	);
}

const ShimmerBox = styled.div`
	height: 4px;
	width: 200px;
	z-index: 0;
	background-image: linear-gradient(90deg, rgba(196, 196, 196, 0.4) 0%, rgba(196, 196, 196, 0) 101.95%);
`;

interface CardProps {
	right?: number;
	bottom?: number;
	scale?: number;
	dive?: number;
	delay?: number;
	bounciness?: number;
	icon: IIconType;
	patter?: 'reverse' | 'forward';
}

function Card({
	right: rightDefault = 0,
	bottom: bottomDefault = 0,
	scale: scaleDefault = 1,
	delay = 0.3,
	dive = 20,
	bounciness = 3,
	patter = 'forward',
	icon,
}: CardProps): JSX.Element | null {
	const breakpoint = useBreakpoint();
	const validBreakpoint = breakpoint & (BreakPoints.Large | BreakPoints.Medium);
	const right = breakpoint === BreakPoints.Medium ? clamp(rightDefault * 0.8, [-10, 100]) : rightDefault;
	const bottom = breakpoint === BreakPoints.Medium ? clamp(bottomDefault * 0.8, [0, 100]) : bottomDefault;

	const y = patter === 'forward' ? [dive, 0, dive] : [0, dive, 0];
	const rotate =
		patter === 'forward'
			? [bounciness, -bounciness, bounciness, -bounciness, bounciness]
			: [-bounciness, bounciness, -bounciness, bounciness, -bounciness];
	const baseDuration = 8;
	const scale = breakpoint !== BreakPoints.Large ? scaleDefault * 0.75 : scaleDefault;

	return (
		<motion.div
			style={{
				zIndex: 0,
				right: `${right}%`,
				bottom: `${bottom}%`,
				position: 'absolute',
				display: validBreakpoint ? 'inline-block' : 'none',
				transformOrigin: 'center center',
			}}
			initial={{
				y: 50,
				rotate: 2,
				opacity: 0,
				scale: scale / 4,
			}}
			animate={{
				y: y,
				rotate: rotate,
				opacity: 1,
				scale: scale,
				transition: {
					delay: delay,
					opacity: {
						duration: 1,
					},
					scale: {
						type: 'spring',
					},
					y: {
						duration: baseDuration,
						repeat: Infinity,
						ease: 'easeInOut',
					},
					rotate: {
						duration: baseDuration * 3,
						repeat: Infinity,
						ease: 'easeInOut',
					},
				},
			}}
		>
			<Box
				background='white'
				height='80px'
				width='300px'
				direction='row'
				borderRadius={tokens.borderRadius.large}
				align='center'
				padding={tokens.spacing.x1}
				boxShadow={tokens.elevation.flying}
			>
				<Avatar icon={<IIcon type={icon} />} size='large' />

				<Spacer vertical={tokens.spacing.x2} />
				<Box height='100px' width='250px' padding={'20px'}>
					<Spacer vertical={tokens.spacing.x1_5} />
					<ShimmerBox />
					<Spacer vertical={tokens.spacing.x1_5} />
					<ShimmerBox />
					<Spacer vertical={tokens.spacing.x1_5} />
					<ShimmerBox />
					<Spacer vertical={tokens.spacing.x1_5} />
				</Box>
			</Box>
		</motion.div>
	);
}

const StyledArrowAligner = styled(motion.div)`
	display: flex;
	justify-content: center;
	align-items: center;
	height: ${tokens.size.x4};
	position: absolute;
	left: 0;
	right: 0;
	bottom: 0;
	z-index: 1;
`;

const StyledArrowDown = styled.button`
	all: unset;
	width: ${tokens.size.x4};
	height: ${tokens.size.x4};
	border-radius: 50%;
	display: flex;
	justify-content: center;
	align-items: center;
	border: 2px solid ${tokens.color.content.interactive};
	color: ${tokens.color.content.interactive};
	transition: opacity 0.3s ease-in-out;
	backdrop-filter: blur(20px);
	opacity: 1;

	${tokens.breakpointQuery.small} {
		opacity: 0.8;

		font: ${tokens.text.heading.xSmall};
		&:hover,
		&:focus {
			opacity: 1;
		}
	}
`;

function ArrowDown(): JSX.Element {
	const { scrollY } = useScroll();
	const [show, setShow] = useState(() => scrollY.get() <= 200);
	const ref = useRef<HTMLButtonElement>(null);
	const wasMounted = useRef(false);

	function handleScrollDownClick(): void {
		ref.current?.scrollIntoView({ behavior: 'smooth' });
		ref.current?.blur();
	}

	useEffect(() => {
		return scrollY.on('change', (latest) => {
			setShow(latest <= 200);
		});
	}, [scrollY]);

	useEffect(() => {
		wasMounted.current = true;
	}, []);

	return (
		<AnimatePresence initial>
			{show ? (
				<StyledArrowAligner
					initial={{
						opacity: 0,
						y: 40,
					}}
					animate={{
						opacity: 1,
						y: 0,
						transition: {
							type: 'spring',
							delay: wasMounted.current ? 0.3 : 1.2,
						},
					}}
					exit={{
						opacity: 0,
						y: 20,
						transition: {
							type: 'spring',
							delay: 0.3,
						},
					}}
				>
					<Tooltip content={withTranslation('PW.Component.Hero.ScrollDown')}>
						<StyledArrowDown onClick={handleScrollDownClick} ref={ref} tabIndex={-1}>
							<motion.div
								style={{ display: 'flex' }}
								animate={{
									translateY: [4, 0, 4],
									transition: {
										translateY: {
											duration: 2.5,
											repeat: Infinity,
											ease: 'easeOut',
										},
									},
								}}
							>
								<Icon size='small' type='arrowRight' rotation={90} />
							</motion.div>
						</StyledArrowDown>
					</Tooltip>
				</StyledArrowAligner>
			) : null}
		</AnimatePresence>
	);
}
